import React, { useState, useMemo } from 'react';
import { useInventory } from '../context/InventoryContext';
import { useAuth } from '../context/AuthContext';
import { FaTrash, FaPlus, FaEdit } from 'react-icons/fa';
import Pagination from '../components/Pagination';

const InstallerSignup = () => {
  const { companies = [], subCompanies = [], getEntityDisplayName } = useInventory();
  const { users = [], addUser, deleteUser, updateUser, currentUser } = useAuth();
  
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isTeamLead = currentUser?.role === 'teamlead';

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);

  const [form, setForm] = useState({
    username: '',
    password: '',
    phone: '',
    companyId: '',
    subCompanyId: '',
    teamLeadId: '',
    mileage: ''
  });
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ message: '', type: '', visible: false });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [searchQuery, setSearchQuery] = useState('');

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type, visible: true });
    setTimeout(() => {
      setNotification({ message: '', type: '', visible: false });
    }, 3000);
  };

  const allTeamLeads = useMemo(() => {
    return users.filter(u => 
      String(u.role).trim().toLowerCase() === "teamlead"
    );
  }, [users]);

  const installers = useMemo(() => {
    return users.filter(u => String(u.role).trim().toLowerCase() === "installer");
  }, [users]);

  const companySubs = useMemo(() => {
    return subCompanies.filter(s => String(s.parentCompanyId) === String(form.companyId));
  }, [subCompanies, form.companyId]);

  // Team Lead filtering for installers
  const filteredInstallers = useMemo(() => {
    let baseInstallers = installers;
    
    if (isTeamLead) {
      baseInstallers = baseInstallers.filter(inst => 
        String(inst.teamLeadId) === String(currentUser.id)
      );
    }
    
    return baseInstallers.filter(inst => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      const companyName = companies.find(c => c.id === inst.companyId)?.name || '';
      const subName = inst.subCompanyId
        ? subCompanies.find(s => s.id === inst.subCompanyId)?.name || ''
        : '';
      const teamLeadName = allTeamLeads.find(tl => tl.id === inst.teamLeadId)?.username || '';
      return (
        inst.username.toLowerCase().includes(q) ||
        (inst.phone || '').includes(q) ||
        companyName.toLowerCase().includes(q) ||
        subName.toLowerCase().includes(q) ||
        teamLeadName.toLowerCase().includes(q) ||
        (inst.mileage || '').toLowerCase().includes(q)
      );
    });
  }, [installers, searchQuery, companies, subCompanies, allTeamLeads, isTeamLead, currentUser]);

  const totalItems = filteredInstallers.length;
  const paginatedInstallers = useMemo(() => {
    return filteredInstallers.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize
    );
  }, [filteredInstallers, currentPage, pageSize]);

  const resetForm = () => {
    setForm({ 
      username: '', 
      password: '', 
      phone: '', 
      companyId: '', 
      subCompanyId: '',
      teamLeadId: '',
      mileage: ''
    });
    setError('');
    setIsEditing(false);
    setEditingUserId(null);
  };

  const handleEditClick = (installer) => {
    setError('');
    setIsEditing(true);
    setEditingUserId(installer.id);
    setForm({
      username: installer.username,
      phone: installer.phone || '',
      companyId: installer.companyId || '',
      subCompanyId: installer.subCompanyId || '',
      teamLeadId: installer.teamLeadId || '',
      mileage: installer.mileage || '',
      password: ''
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isEditing) {
      if (!form.username || !form.phone || !form.companyId) {
        setError('Username, phone and company fields are required.');
        return;
      }
    } else {
      if (!form.username || !form.password || !form.phone || !form.companyId) {
        setError('All fields are required.');
        return;
      }
    }

    if (!/^\d{10}$/.test(form.phone)) {
      setError('Phone must be exactly 10 digits.');
      return;
    }

    if (form.mileage && form.mileage.trim() !== '') {
      if (isNaN(form.mileage.trim())) {
        setError('Mileage must be a number.');
        return;
      }
      if (Number(form.mileage.trim()) < 0) {
        setError('Mileage must be a positive number.');
        return;
      }
    }

    const finalTeamLeadId = isTeamLead ? currentUser.id : form.teamLeadId;

    if (isEditing) {
      const updatedData = {
        username: form.username,
        phone: form.phone,
        companyId: form.companyId,
        subCompanyId: form.subCompanyId || null,
        teamLeadId: finalTeamLeadId || null,
        mileage: form.mileage || null,
        role: 'installer'
      };

      if (form.password && form.password.trim() !== '') {
        updatedData.password = form.password;
      }

      if (updateUser) {
        await updateUser(editingUserId, updatedData);
        showNotification(`Installer "${form.username}" has been updated successfully`, 'success');
      }
    } else {
      await addUser({
        username: form.username,
        password: form.password,
        phone: form.phone,
        companyId: form.companyId,
        subCompanyId: form.subCompanyId || null,
        teamLeadId: finalTeamLeadId || null,
        mileage: form.mileage || null,
        role: 'installer'
      });
      showNotification(`Installer "${form.username}" has been added successfully`, 'success');
    }

    resetForm();
    setShowForm(false);
  };

  const handleDelete = (id) => {
    const installerToDelete = users.find(u => u.id === id && String(u.role).trim().toLowerCase() === "installer");
    if (installerToDelete && window.confirm(`Are you sure you want to delete installer "${installerToDelete.username}"?`)) {
      deleteUser(id, "installer");
      showNotification(`Installer "${installerToDelete.username}" has been deleted`, 'error');
    }
  };

  return (
    <div>
      {notification.visible && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-xl shadow-lg transition-all duration-300 ${
          notification.type === 'error' ? 'bg-red-500' : 'bg-green-500'
        } text-white font-medium`}>
          {notification.message}
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Installer Management</h1>
        <div className="flex items-center gap-3">
          {(isSuperAdmin || isTeamLead) && (
            <button onClick={() => { resetForm(); setShowForm(true); }} className="btn-primary">
              <FaPlus /> Create Installer
            </button>
          )}
        </div>
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by username, phone, company, sub‑company, team lead, mileage…"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
          className="w-full max-w-md rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Username</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Sub‑Company</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Team Lead</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Mileage</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {paginatedInstallers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-sm text-gray-500">
                    {isTeamLead ? 'No installers assigned to you.' : 'No installers created yet.'}
                  </td>
                </tr>
              ) : (
                paginatedInstallers.map((installer) => {
                  const teamLead = allTeamLeads.find(tl => tl.id === installer.teamLeadId);
                  return (
                    <tr key={installer.id} className="hover:bg-indigo-50/50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{installer.username}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{installer.phone || '-'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {companies.find(c => String(c.id) === String(installer.companyId))?.name || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {installer.subCompanyId ? getEntityDisplayName(installer.subCompanyId) : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {teamLead ? (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                            {teamLead.username}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {installer.mileage ? (
                          <span className="font-medium text-indigo-600">{installer.mileage}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm flex items-center space-x-3">
                        {/* ✅ Show Edit & Delete for both Super Admin and Team Lead */}
                        {(isSuperAdmin || isTeamLead) && (
                          <>
                            <button 
                              onClick={() => handleEditClick(installer)} 
                              className="text-indigo-600 hover:text-indigo-900 transition-colors"
                              title="Edit Installer"
                            >
                              <FaEdit className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleDelete(installer.id)} 
                              className="text-red-500 hover:text-red-700 transition-colors"
                              title="Delete Installer"
                            >
                              <FaTrash className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        totalItems={totalItems}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
      />

      {showForm && (isSuperAdmin || isTeamLead) && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-md mx-auto my-8 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              {isEditing ? "Update Installer Account" : "Create Installer"}
            </h2>
            {error && <div className="text-red-600 text-sm mb-4">{error}</div>}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={form.username}
                  onChange={e => setForm({...form, username: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isEditing ? "New Password (Leave blank to keep unchanged)" : "Password"}
                </label>
                <input
                  type="password"
                  placeholder={isEditing ? "••••••••" : ""}
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  required={!isEditing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone No (10 digits)</label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={form.phone}
                  onChange={e => setForm({...form, phone: e.target.value.replace(/\D/g, '')})}
                  maxLength={10}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <select
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                  value={form.companyId}
                  onChange={e => setForm({...form, companyId: e.target.value, subCompanyId: ''})}
                  required
                >
                  <option value="">Select Company</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              {companySubs.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sub‑Company (optional)</label>
                  <select
                    className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                    value={form.subCompanyId}
                    onChange={e => setForm({...form, subCompanyId: e.target.value})}
                  >
                    <option value="">-- No Sub‑Company --</option>
                    {companySubs.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}

              {!isTeamLead && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Team Lead (optional)</label>
                  <select
                    className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                    value={form.teamLeadId}
                    onChange={e => setForm({...form, teamLeadId: e.target.value})}
                  >
                    <option value="">Select Team Lead</option>
                    {allTeamLeads.map(tl => (
                      <option key={tl.id} value={tl.id}>
                        {tl.username}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {isTeamLead && (
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700">
                    <span className="font-semibold">Team Lead:</span> {currentUser?.username} (Auto-assigned)
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mileage (optional)
                </label>
                <input
                  type="text"
                  placeholder="Enter mileage..."
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={form.mileage}
                  onChange={e => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setForm({...form, mileage: value});
                    }
                  }}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => { resetForm(); setShowForm(false); }} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">
                  {isEditing ? "Save Changes" : "Create Installer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstallerSignup;